import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';

import {
  CHANNEL_COLOR_OPTIONS,
  ColorMode,
  resolveChannelColorToken,
} from 'actions/agent_preferences/lib/channel_color_constants';
import { cssVarForChannelColor } from './channel_color_css_var';
import connect from 'components/lib/connect';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import { EndpointTypes } from 'models/endpoint';
import RadioGroup from 'components/common/radio_group';
import SetChannelColorMode from 'actions/agent_preferences/set_channel_color_mode';
import UpdateChannelColor from 'actions/agent_preferences/update_channel_color';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const COLOR_MODE_OPTIONS = [
  { label: 'No custom colors', value: ColorMode.NONE },
  { label: 'Border colors per channel', value: ColorMode.PER_CHANNEL },
];

const CHANNEL_LABELS = Object.freeze({
  [EndpointTypes.CHAT]: 'Chat',
  [EndpointTypes.CUSTOM_CHANNEL]: 'Custom Channel',
  [EndpointTypes.EMAIL]: 'Email',
  [EndpointTypes.INSTAGRAM_COMMENT]: 'Instagram Comment',
  [EndpointTypes.INSTAGRAM_DIRECT]: 'Instagram Direct',
  [EndpointTypes.SMS]: 'SMS',
  [EndpointTypes.FB_MESSENGER]: 'Facebook Messenger',
  [EndpointTypes.VOICE]: 'Voice',
  [EndpointTypes.WHATSAPP]: 'WhatsApp',
  [EndpointTypes.TWITTER]: 'Twitter',
});

const CHANNELS_IN_ORDER = Object.freeze([
  EndpointTypes.CHAT,
  EndpointTypes.SMS,
  EndpointTypes.EMAIL,
  EndpointTypes.VOICE,
  EndpointTypes.FB_MESSENGER,
  EndpointTypes.WHATSAPP,
  EndpointTypes.INSTAGRAM_DIRECT,
  EndpointTypes.INSTAGRAM_COMMENT,
  EndpointTypes.TWITTER,
  EndpointTypes.CUSTOM_CHANNEL,
]);

export function ChannelColors({ channelColors, isCustomizeEnabled }) {
  const executeAction = useExecuteAction();

  const onSelect = useCallback(
    (channel, color) => {
      executeAction(UpdateChannelColor, { channel, color });
    },
    [executeAction]
  );

  const currentMode = isCustomizeEnabled ? ColorMode.PER_CHANNEL : ColorMode.NONE;

  const onModeChange = useCallback(
    event => {
      const mode = event.target.value;
      if (mode !== currentMode) executeAction(SetChannelColorMode, { mode });
    },
    [currentMode, executeAction]
  );

  return (
    <div>
      <RadioGroup data-aid="colorMode" onChange={onModeChange} options={COLOR_MODE_OPTIONS} value={currentMode} />

      {isCustomizeEnabled
        ? CHANNELS_IN_ORDER.map(channel => {
            const currentValue = resolveChannelColorToken(channelColors, channel);
            const label = CHANNEL_LABELS[channel];
            return (
              <ChannelRow key={channel}>
                <ChannelLabel>{label}</ChannelLabel>
                <DropdownWrapper>
                  <DropdownMenu
                    data-aid={`channelColor-${channel}`}
                    onSelect={color => onSelect(channel, color)}
                    options={COLOR_DROPDOWN_OPTIONS}
                    value={currentValue}
                  />
                </DropdownWrapper>
              </ChannelRow>
            );
          })
        : null}
    </div>
  );
}

ChannelColors.propTypes = {
  channelColors: PropTypes.object.isRequired,
  isCustomizeEnabled: PropTypes.bool.isRequired,
};

const ChannelColorsContainer = connect(mapStateToProps)(ChannelColors);

function mapStateToProps({ getProvider }) {
  const preferences = getProvider('agentPreferences').get();

  return {
    channelColors: preferences.channelColors || {},
    isCustomizeEnabled: !!preferences.customizeChannelColors,
  };
}

const ChannelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChannelLabel = styled.div`
  flex: 1;
`;

const DropdownWrapper = styled.div`
  flex: 0 0 200px;
  width: 200px;

  > * {
    width: 100%;
  }
`;

const ColorSwatch = styled.span`
  border: 1px solid var(--color-gray300);
  border-radius: 4px;
  display: inline-block;
  height: 16px;
  width: 16px;
`;

const ColorOptionLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

// Each palette color rendered as a swatch beside its name for the dropdown.
// Static — derived only from the frozen palette — so it's built once here.
const COLOR_DROPDOWN_OPTIONS = CHANNEL_COLOR_OPTIONS.map(option => ({
  value: option.value,
  label: (
    <ColorOptionLabel>
      <ColorSwatch style={{ backgroundColor: cssVarForChannelColor(option.value) }} />
      <span>{option.label}</span>
    </ColorOptionLabel>
  ),
}));

export default ChannelColorsContainer;
